// public/js/utils/dateUtils.js

/**
 * Date Utilities
 * Centralized date formatting functions for consistent date display across the application
 * Supports configurable user preferences for date and time formats
 */

/**
 * User Preferences for Date/Time Formatting
 * This will be populated from user settings in the future
 * Currently uses European format as default
 */
let userDateTimePreferences = {
    dateFormat: 'DD.MM.YYYY',      // Options: 'DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'
    dateSeparator: '.',             // Options: '.', '/', '-'
    timeFormat: '24h',              // Options: '24h', '12h'
    timeSeparator: ':',             // Options: ':'
    dateTimeJoiner: ' ',            // Character(s) between date and time
    includeSeconds: false,          // Whether to show seconds in time display
    monthFormat: 'numeric',         // Options: 'numeric' (01), 'short' (Jan), 'long' (January)
    yearFormat: 'full',             // Options: 'full' (2025), 'short' (25)
};

/**
 * Set user date/time preferences
 * This will be called from the settings panel in the future
 * @param {Object} preferences - User preferences object
 */
export function setDateTimePreferences(preferences) {
    userDateTimePreferences = { ...userDateTimePreferences, ...preferences };
}

/**
 * Get current user date/time preferences
 * @returns {Object} Current preferences
 */
export function getDateTimePreferences() {
    return { ...userDateTimePreferences };
}

/**
 * Reset date/time preferences to default (European format)
 */
export function resetDateTimePreferences() {
    userDateTimePreferences = {
        dateFormat: 'DD.MM.YYYY',
        dateSeparator: '.',
        timeFormat: '24h',
        timeSeparator: ':',
        dateTimeJoiner: ' ',
        includeSeconds: false,
        monthFormat: 'numeric',
        yearFormat: 'full',
    };
}

/**
 * Available date format presets for user selection
 */
export const DATE_FORMAT_PRESETS = {
    EUROPEAN: {
        name: 'European (DD.MM.YYYY)',
        dateFormat: 'DD.MM.YYYY',
        dateSeparator: '.',
        example: '15.01.2025'
    },
    US: {
        name: 'US (MM/DD/YYYY)',
        dateFormat: 'MM/DD/YYYY',
        dateSeparator: '/',
        example: '01/15/2025'
    },
    ISO: {
        name: 'ISO (YYYY-MM-DD)',
        dateFormat: 'YYYY-MM-DD',
        dateSeparator: '-',
        example: '2025-01-15'
    },
    UK: {
        name: 'UK (DD/MM/YYYY)',
        dateFormat: 'DD/MM/YYYY',
        dateSeparator: '/',
        example: '15/01/2025'
    }
};

/**
 * Available time format presets for user selection
 */
export const TIME_FORMAT_PRESETS = {
    HOUR_24: {
        name: '24-hour (HH:MM)',
        timeFormat: '24h',
        includeSeconds: false,
        example: '14:30'
    },
    HOUR_24_SEC: {
        name: '24-hour with seconds (HH:MM:SS)',
        timeFormat: '24h',
        includeSeconds: true,
        example: '14:30:45'
    },
    HOUR_12: {
        name: '12-hour (hh:mm AM/PM)',
        timeFormat: '12h',
        includeSeconds: false,
        example: '2:30 PM'
    },
    HOUR_12_SEC: {
        name: '12-hour with seconds (hh:mm:ss AM/PM)',
        timeFormat: '12h',
        includeSeconds: true,
        example: '2:30:45 PM'
    }
};

/**
 * Format date according to user preferences
 * @param {string|Date} date - Date value to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time
 * @param {boolean} options.includeSeconds - Override for including seconds
 * @returns {string} Formatted date string
 */
export function formatDate(date, options = {}) {
    if (!date) return 'Not specified';

    try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) {
            return 'Invalid date';
        }

        const prefs = userDateTimePreferences;
        const includeTime = options.includeTime ?? false;
        const includeSeconds = options.includeSeconds ?? prefs.includeSeconds;

        // Format date part
        let datePart = '';
        const day = dateObj.getDate().toString().padStart(2, '0');
        const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
        const year = prefs.yearFormat === 'short'
            ? dateObj.getFullYear().toString().slice(-2)
            : dateObj.getFullYear();

        // Build date string based on format preference
        switch (prefs.dateFormat) {
            case 'DD.MM.YYYY':
            case 'DD/MM/YYYY':
                datePart = `${day}${prefs.dateSeparator}${month}${prefs.dateSeparator}${year}`;
                break;
            case 'MM/DD/YYYY':
                datePart = `${month}${prefs.dateSeparator}${day}${prefs.dateSeparator}${year}`;
                break;
            case 'YYYY-MM-DD':
                datePart = `${year}${prefs.dateSeparator}${month}${prefs.dateSeparator}${day}`;
                break;
            default:
                datePart = `${day}${prefs.dateSeparator}${month}${prefs.dateSeparator}${year}`;
        }

        // Add time if requested
        if (includeTime) {
            let timePart = '';
            let hours = dateObj.getHours();
            const minutes = dateObj.getMinutes().toString().padStart(2, '0');
            const seconds = dateObj.getSeconds().toString().padStart(2, '0');

            if (prefs.timeFormat === '12h') {
                const ampm = hours >= 12 ? 'PM' : 'AM';
                hours = hours % 12 || 12; // Convert to 12-hour format
                timePart = `${hours}${prefs.timeSeparator}${minutes}`;
                if (includeSeconds) {
                    timePart += `${prefs.timeSeparator}${seconds}`;
                }
                timePart += ` ${ampm}`;
            } else {
                // 24-hour format
                hours = hours.toString().padStart(2, '0');
                timePart = `${hours}${prefs.timeSeparator}${minutes}`;
                if (includeSeconds) {
                    timePart += `${prefs.timeSeparator}${seconds}`;
                }
            }

            return `${datePart}${prefs.dateTimeJoiner}${timePart}`;
        }

        return datePart;
    } catch (error) {
        console.warn('Date formatting failed:', error);
        return date.toString();
    }
}


/**
 * Format date based on field type and schema configuration
 * This is the main formatting function that should be used throughout the application
 *
 * @param {string|Date} value - Date value to format
 * @param {Object} fieldSchema - Field schema with type and dateFormat information
 * @returns {string} Formatted date string
 *
 * @example
 * // Date field with date-only format
 * formatDateBySchema('2025-01-15', { type: 'date', dateFormat: 'date-only' })
 * // Returns: "15.01.2025" (with current user preferences)
 *
 * // Datetime field
 * formatDateBySchema('2025-01-15T10:30:00Z', { type: 'datetime-local', dateFormat: 'date-time' })
 * // Returns: "15.01.2025 10:30" (with current user preferences)
 */
export function formatDateBySchema(value, fieldSchema) {
    if (!value) return 'Not specified';

    const type = fieldSchema?.type;
    const dateFormat = fieldSchema?.dateFormat;

    // Handle different field types
    switch (type) {
        case 'date':
            // Date type: respect dateFormat setting
            const includeTime = dateFormat !== 'date-only';
            return formatDate(value, { includeTime: includeTime });

        case 'datetime-local':
        case 'datetime':
            // Datetime types: always include time
            return formatDate(value, { includeTime: true });

        default:
            // Unknown type, try to format as date with time
            return formatDate(value, { includeTime: true });
    }
}

/**
 * Parse a date string according to user preferences back to ISO string
 * Supports European, US, ISO, and UK formats
 * @param {string} dateString - Date string in user's preferred format
 * @returns {string|null} ISO date string or null if parsing fails
 *
 * @example
 * // With European format preference
 * parseDateString('15.01.2025') // Returns: "2025-01-15T00:00:00.000Z"
 * parseDateString('15.01.2025 10:30') // Returns: "2025-01-15T10:30:00.000Z"
 *
 * // With US format preference
 * parseDateString('01/15/2025') // Returns: "2025-01-15T00:00:00.000Z"
 */
export function parseDateString(dateString) {
    if (!dateString) return null;

    try {
        const prefs = userDateTimePreferences;
        let pattern;
        let dayIndex, monthIndex, yearIndex;

        // Determine parsing pattern based on user format
        switch (prefs.dateFormat) {
            case 'DD.MM.YYYY':
            case 'DD/MM/YYYY':
                dayIndex = 1;
                monthIndex = 2;
                yearIndex = 3;
                break;
            case 'MM/DD/YYYY':
                monthIndex = 1;
                dayIndex = 2;
                yearIndex = 3;
                break;
            case 'YYYY-MM-DD':
                yearIndex = 1;
                monthIndex = 2;
                dayIndex = 3;
                break;
            default:
                dayIndex = 1;
                monthIndex = 2;
                yearIndex = 3;
        }

        // Build regex pattern (supports multiple separators and optional time)
        const sepRegex = '[./-]';
        const timeRegex = '(?:\\s+(\\d{1,2}):(\\d{1,2})(?::(\\d{1,2}))?(?:\\s*([AP]M))?)?';
        pattern = new RegExp(`^(\\d{1,4})${sepRegex}(\\d{1,2})${sepRegex}(\\d{2,4})${timeRegex}$`, 'i');

        const match = dateString.match(pattern);
        if (!match) return null;

        const day = parseInt(match[dayIndex], 10);
        const month = parseInt(match[monthIndex], 10);
        let year = parseInt(match[yearIndex], 10);

        // Handle 2-digit years
        if (year < 100) {
            year += year < 50 ? 2000 : 1900;
        }

        let hours = match[4] ? parseInt(match[4], 10) : 0;
        const minutes = match[5] ? parseInt(match[5], 10) : 0;
        const seconds = match[6] ? parseInt(match[6], 10) : 0;
        const ampm = match[7];

        // Convert 12-hour to 24-hour format if needed
        if (ampm) {
            if (ampm.toUpperCase() === 'PM' && hours < 12) hours += 12;
            if (ampm.toUpperCase() === 'AM' && hours === 12) hours = 0;
        }

        // Create date object (month is 0-indexed in JavaScript)
        const dateObj = new Date(year, month - 1, day, hours, minutes, seconds);

        // Validate the date
        if (isNaN(dateObj.getTime())) return null;

        return dateObj.toISOString();
    } catch (error) {
        console.warn('Date parsing failed:', error);
        return null;
    }
}


/**
 * Validate and normalize a date value for storage
 * Accepts various date formats and returns ISO string
 *
 * @param {string|Date} value - Date value to normalize
 * @returns {string|null} ISO date string or null if invalid
 */
export function normalizeDateForStorage(value) {
    if (!value) return null;

    try {
        // If it's already a Date object
        if (value instanceof Date) {
            return value.toISOString();
        }

        // Try parsing with user's format first
        const userParsed = parseDateString(value);
        if (userParsed) return userParsed;

        // Try parsing as standard date
        const dateObj = new Date(value);
        if (!isNaN(dateObj.getTime())) {
            return dateObj.toISOString();
        }

        return null;
    } catch (error) {
        console.warn('Date normalization failed:', error);
        return null;
    }
}

/**
 * Format field value for HTML form inputs (datetime-local, date, etc.)
 * Converts ISO dates to format expected by HTML input elements
 * NOTE: HTML inputs always use ISO format (YYYY-MM-DD) regardless of user preferences
 *
 * @param {string|Date} value - Date value
 * @param {Object} fieldConfig - Field configuration with type information
 * @returns {string} Formatted value for form input
 */
export function formatDateForFormInput(value, fieldConfig) {
    if (!value) return '';

    try {
        const dateObj = new Date(value);
        if (isNaN(dateObj.getTime())) return '';

        const type = fieldConfig?.type;

        switch (type) {
            case 'datetime-local':
                // Format: YYYY-MM-DDTHH:MM (HTML5 standard)
                const year = dateObj.getFullYear();
                const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
                const day = dateObj.getDate().toString().padStart(2, '0');
                const hours = dateObj.getHours().toString().padStart(2, '0');
                const minutes = dateObj.getMinutes().toString().padStart(2, '0');
                return `${year}-${month}-${day}T${hours}:${minutes}`;

            case 'date':
                // Format: YYYY-MM-DD (HTML5 standard)
                return dateObj.toISOString().split('T')[0];

            default:
                return value.toString();
        }
    } catch (error) {
        console.warn('Form input date formatting failed:', error);
        return '';
    }
}

/**
 * Get example format string for current user preferences
 * Useful for placeholder text in forms
 * @param {boolean} includeTime - Whether to include time in example
 * @returns {string} Example date format string
 */
export function getDateFormatExample(includeTime = false) {
    const prefs = userDateTimePreferences;
    const exampleDate = formatDate(new Date(2025, 0, 15, 14, 30, 45), {
        includeTime,
        includeSeconds: prefs.includeSeconds
    });
    return exampleDate;
}

/**
 * Get format mask string for current user preferences
 * Useful for input validation and masking
 * @param {boolean} includeTime - Whether to include time
 * @returns {string} Format mask (e.g., "DD.MM.YYYY HH:MM")
 */
export function getDateFormatMask(includeTime = false) {
    const prefs = userDateTimePreferences;
    let mask = prefs.dateFormat;

    if (includeTime) {
        const timeMask = prefs.timeFormat === '12h'
            ? (prefs.includeSeconds ? 'hh:mm:ss AM/PM' : 'hh:mm AM/PM')
            : (prefs.includeSeconds ? 'HH:MM:SS' : 'HH:MM');
        mask += prefs.dateTimeJoiner + timeMask;
    }

    return mask;
}

// Export all functions
export default {
    // Main functions
    formatDate,
    formatDateBySchema,
    parseDateString,
    normalizeDateForStorage,
    formatDateForFormInput,

    // Preference management
    setDateTimePreferences,
    getDateTimePreferences,
    resetDateTimePreferences,

    // Helper functions
    getDateFormatExample,
    getDateFormatMask,

    // Presets for settings UI
    DATE_FORMAT_PRESETS,
    TIME_FORMAT_PRESETS
};
